/**
 * CSV Import/Export Utilities
 * Provides functions for converting data to/from CSV format
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Optional array of column definitions {field, header}
 * @returns {string} CSV string
 */
export const exportToCSV = (data, columns = null) => {
  if (!data || data.length === 0) {
    return '';
  }

  // If columns not provided, use all keys from first object
  const headers = columns 
    ? columns.map(col => col.header || col.field)
    : Object.keys(data[0]);
  
  const fields = columns
    ? columns.map(col => col.field)
    : Object.keys(data[0]);

  // Create CSV header row
  const csvHeader = headers.map(h => `"${h}"`).join(',');

  // Create CSV data rows
  const csvRows = data.map(row => {
    return fields.map(field => {
      let value = getNestedValue(row, field);
      
      // Handle different data types
      if (value === null || value === undefined) {
        return '""';
      }
      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }
      
      // Escape quotes and wrap in quotes
      value = String(value).replace(/"/g, '""');
      return `"${value}"`;
    }).join(',');
  });

  return [csvHeader, ...csvRows].join('\n');
};

/**
 * Get nested object value by dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'user.name')
 * @returns {*} Value at path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = 'export.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse CSV string to array of objects
 * @param {string} csvContent - CSV content string
 * @param {Object} options - Parsing options
 * @returns {Array} Array of objects
 */
export const parseCSV = (csvContent, options = {}) => {
  const {
    delimiter = ',',
    skipEmptyLines = true,
    trimValues = true
  } = options;

  const lines = csvContent.split('\n');
  if (lines.length === 0) return [];

  // Parse header row
  const headers = parseCSVLine(lines[0], delimiter, trimValues);
  
  // Parse data rows
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    
    if (skipEmptyLines && !line.trim()) {
      continue;
    }

    const values = parseCSVLine(line, delimiter, trimValues);
    
    // Create object from headers and values
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    data.push(row);
  }

  return data;
};

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line to parse
 * @param {string} delimiter - Field delimiter
 * @param {boolean} trim - Whether to trim values
 * @returns {Array} Array of values
 */
const parseCSVLine = (line, delimiter = ',', trim = true) => {
  const values = [];
  let currentValue = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        // Escaped quote
        currentValue += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(trim ? currentValue.trim() : currentValue);
      currentValue = '';
    } else {
      currentValue += char;
    }
  }

  // Add last value
  values.push(trim ? currentValue.trim() : currentValue);

  return values;
};

/**
 * Read file as text
 * @param {File} file - File object to read
 * @returns {Promise<string>} File content as string
 */
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

/**
 * Validate CSV headers against expected columns
 * @param {Array} headers - CSV headers
 * @param {Array} requiredColumns - Required column names
 * @returns {Object} {valid: boolean, missing: Array}
 */
export const validateCSVHeaders = (headers, requiredColumns) => {
  const missing = requiredColumns.filter(col => !headers.includes(col));
  return {
    valid: missing.length === 0,
    missing
  };
};

/**
 * Format date for CSV export
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForCSV = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0]; // YYYY-MM-DD
};

/**
 * Format currency for CSV export
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrencyForCSV = (amount) => {
  if (amount === null || amount === undefined) return '0.00';
  return Number(amount).toFixed(2);
};
